import { useFormikContext, FieldArray } from 'formik';
import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { InputLabel } from 'src/common';
import MaterialInput from 'src/components/form/MaterialInput';
import MaterialDatePicker from 'src/components/form/MaterialDatePicker';
import UserInput from 'src/components/UserInput';
import { EventCompletionStatusSelect } from 'src/components/select/EventCompletionStatusSelect';
import { EventCompletionStatus, EventInput } from 'src/graphql/_generated';
import { ReactComponent as CrossIcon } from 'src/assets/cross.svg';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ActionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 20px;
  }

  .flex {
    display: flex;
    gap: 30px;

    &__cross-icon {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      svg {
        fill: ${({ theme }) => theme.colors.cflowerBlue};
      }
    }
  }
`;

const FormSeparator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.stroke};
  margin: 10px 0;
`;

interface EventFormProps {
  editMode: boolean;
}

export const EventForm: React.FC<EventFormProps> = ({ editMode }) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = useFormikContext<{
    actions: EventInput[];
  }>();

  const getFieldError = (idx: number, field: string) =>
    _.get(errors, `actions[${idx}].${field}`) && _.get(touched, `actions[${idx}].${field}`);

  const getFieldHelperText = (idx: number, field: string) => {
    if (!_.get(touched, `actions[${idx}].${field}`)) return '';
    const error = _.get(errors, `actions[${idx}].${field}`);
    return typeof error === 'string' ? error : '';
  };

  return (
    <FieldArray name="actions">
      {({ remove }) => (
        <FormContainer>
          {values.actions.map((action, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && values.actions.length > 1 && <FormSeparator />}
              <ActionItem>
                <div>
                  {editMode ? <InputLabel>USER ACTION *</InputLabel> : <InputLabel>USER ACTION {idx + 1}</InputLabel>}
                  <div className="flex">
                    <MaterialInput
                      name={`actions.${idx}.notes`}
                      value={action.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter user action"
                      css="width: 100%;"
                      error={getFieldError(idx, 'notes')}
                      helperText={getFieldHelperText(idx, 'notes')}
                      data-cy="notes-input"
                    />
                    {values.actions.length > 1 && (
                      <div className="flex__cross-icon">
                        <CrossIcon onClick={() => remove(idx)} />
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid">
                  <div>
                    <InputLabel>OWNER *</InputLabel>
                    <UserInput
                      value={action.assignedTo || { text: '' }}
                      onChange={(user) => setFieldValue(`actions.${idx}.assignedTo`, user)}
                      onSelectUser={(user) => setFieldValue(`actions.${idx}.assignedTo`, user)}
                      placeholder="Select owner"
                      onBlur={() => setFieldTouched(`actions.${idx}.assignedTo`, true)}
                      error={getFieldError(idx, 'assignedTo')}
                      helperText={getFieldHelperText(idx, 'assignedTo')}
                    />
                  </div>

                  <div>
                    <InputLabel>DUE DATE *</InputLabel>
                    <MaterialDatePicker
                      value={action.dueDate}
                      onChange={(date) => setFieldValue(`actions.${idx}.dueDate`, date)}
                      inputProps={{
                        placeholder: 'Due date',
                        onBlur: () => setFieldTouched(`actions.${idx}.dueDate`, true),
                        fullWidth: true,
                        error: getFieldError(idx, 'dueDate'),
                        helperText: getFieldHelperText(idx, 'dueDate'),
                        ['data-cy']: 'due-date-input',
                      }}
                    />
                  </div>
                </div>

                {editMode && (
                  <div className="grid">
                    <div>
                      <InputLabel>COMPLETION STATUS</InputLabel>
                      <EventCompletionStatusSelect
                        value={action.completionStatus}
                        onChange={(option) => {
                          setFieldValue(`actions.${idx}.completionStatus`, option.value);
                          if (option.value !== EventCompletionStatus.Done) {
                            setFieldValue(`actions.${idx}.completedAt`, null);
                          }
                        }}
                        onBlur={() => setFieldTouched(`actions.${idx}.completionStatus`, true)}
                        placeholder="Select status"
                        error={getFieldError(idx, 'completionStatus')}
                        helperText={getFieldHelperText(idx, 'completionStatus')}
                      />
                    </div>
                    {action.completionStatus === EventCompletionStatus.Done && (
                      <div>
                        <InputLabel>DATE COMPLETED *</InputLabel>
                        <MaterialDatePicker
                          value={action.completedAt}
                          onChange={(date) => setFieldValue(`actions.${idx}.completedAt`, date)}
                          inputProps={{
                            placeholder: 'Completion date',
                            onBlur: () => setFieldTouched(`actions.${idx}.completedAt`, true),
                            fullWidth: true,
                            error: getFieldError(idx, 'completedAt'),
                            helperText: getFieldHelperText(idx, 'completedAt'),
                            ['data-cy']: 'completed-at-input',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </ActionItem>
            </React.Fragment>
          ))}
        </FormContainer>
      )}
    </FieldArray>
  );
};
